import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { RequestService } from 'clicklease-ui-elements';
import { environment } from '@envs/environment';
import { PromiseToPayFilterRequest, PromiseToPayFilterRequestConditions, PromiseToPayFilterResponse } from '../..';

/**
 * @service
 * @class PromisesService
 * @extends RequestService
 * @description Service responsible for managing Promise-to-Pay (PTP) payment operations.
 * Handles fetching scheduled PTP payments, filtering by application ID, and managing
 * payment promise data. This service integrates with the backend API to retrieve
 * one-time payment commitments and their statuses.
 */
@Injectable({
    providedIn: 'root',
})
export class PromisesService extends RequestService {
    /**
     * @protected
     * @readonly
     * @property serviceBaseUrl
     * @type {string}
     * @description Base URL for the promises API endpoint. Constructed using the
     * environment configuration API_URL with the 'promises/' path appended.
     * Used as the foundation for all HTTP requests to the promises service.
     */
    protected readonly serviceBaseUrl: string = `${environment.API_URL}promises/`;

    /**
     * @public
     * @method getPromiseList
     * @description Retrieves a filtered list of Promise-to-Pay (PTP) payments based on
     * specified conditions. Fetches scheduled one-time payments for a specific application,
     * including their status (PENDING, KEPT, BROKEN, CANCELLED), payment details, and
     * associated bank account information.
     *
     * @param {PromiseToPayFilterRequestConditions} conditions - Filter conditions to query
     * PTP payments. Typically contains field/operator/value triplets to filter by
     * applicationId or other criteria.
     * @example
     * const conditions = { field: 'applicationId', operator: 'equals', value: '2a6c9c1f-...' };
     * service.getPromiseList(conditions).subscribe(response => {
     *   console.log(response.promisePayments); // Array of PTP payments
     * });
     *
     * @returns {Observable<PromiseToPayFilterResponse>} Observable that emits a response
     * containing:
     * - totalRecords: Total number of matching PTP payments
     * - promisePayments: Array of PTP payment objects with details including:
     *   - promiseId: Unique identifier for the promise
     *   - amount: Payment amount
     *   - paymentDate: Scheduled payment date
     *   - paymentStatus: Status (PENDING, KEPT, BROKEN, CANCELLED)
     *   - paymentMethod: Payment method (e.g., ACH)
     *   - bankAccountId: Associated bank account ID
     *   - bankAccountLastFourDigits: Last 4 digits of bank account
     *   - applicationId: Associated lease/contract application ID
     *   - And other metadata (created/modified dates, creator info, etc.)
     * - count: Number of records in current response
     * - totalPages: Total number of pages available
     * - pageSize: Number of records per page (default: 1000)
     * - page: Current page number (0-indexed)
     *
     * @note Currently returns mock data (lines 15-137) for development/testing purposes.
     * The actual API call is defined but commented out (lines 138-144).
     *
     * @httpMethod POST
     * @endpoint promises/payments/filter
     * @contentType application/json
     */
    public getPromiseList(conditions: PromiseToPayFilterRequestConditions): Observable<PromiseToPayFilterResponse> {
        /**
         * @const params
         * @type {PromiseToPayFilterRequest}
         * @description Constructs the request payload for filtering PTP payments.
         * Creates a nested filter structure wrapping the provided conditions with:
         * - filter: Array containing expression groups
         * - expressions: Array containing condition sets
         * - conditions: Array containing the actual filter conditions (e.g., applicationId match)
         * - page: Current page number (0 for first page)
         * - pageSize: Maximum number of records to return (1000)
         */
        const params: PromiseToPayFilterRequest = { filter: [{ expressions: [{ conditions: [conditions] }] }], page: 0, pageSize: 1000 };
        return this.post<PromiseToPayFilterResponse>({
            url: 'payments/filter',
            params,
            headers: {
                'content-type': 'application/json',
            },
        });
    }
}